import { service } from "@ember/service";
import Controller from "@ember/controller";
import { action } from "@ember/object";
import RSVP from "rsvp";
import { task } from "ember-concurrency";
import { tracked } from "@glimmer/tracking";
import destroyRecord from "glesys-controlpanel/utils/destroy-record";
import ENV from "glesys-controlpanel/config/environment";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

export default class ServerDetailsController extends Controller {
  @service ajax;
  @service http;
  @service modal;
  @service router;
  @service store;
  @service cost;
  @service abilities;

  get project() {
    return this.model.project;
  }

  get networks() {
    return this.model.networks;
  }

  get server() {
    return this.model.server;
  }

  get canEditBackup() {
    return this.server.isVMware;
  }

  @tracked editableDisk;
  @tracked editableMemory;
  @tracked editableCpu;

  get supportsEditCpuCores() {
    return this.server.supportsEditCpuCores;
  }

  get supportsEditMemorySize() {
    return this.server.supportsEditMemorySize;
  }

  get supportsEditDiskSize() {
    return this.server.supportsEditDiskSize;
  }

  get supportsEditIp() {
    return this.server.supportsEditIp;
  }

  get platform() {
    return this.server.platform;
  }

  get args() {
    return this.server.platformArguments;
  }

  get diskValues() {
    return this.args.get("diskSizes").map((diskSize) => diskSize.value);
  }

  get memoryValues() {
    return this.args.get("memorySizes").map((memorySize) => memorySize.value);
  }

  get cpuValues() {
    return this.args.get("cpuCores").map((cpuCore) => cpuCore.value);
  }

  get hasResellerPricing() {
    return this.abilities.cannot("access pricing for organization", this.project.organization);
  }

  // Sort by version:asc address:desc
  get sortedIps() {
    return this.server
      .hasMany("ips")
      .value()
      ?.slice()
      .sort((a, b) => {
        return a.version - b.version || a.address.replaceAll(/[.:]/g, "") - b.address.replaceAll(/[.:]/g, "");
      });
  }

  get serverResourcesIsDirty() {
    let { cpucores, memorysize, disksize } = this.server;
    return cpucores !== this.editableCpu || memorysize !== this.editableMemory || disksize !== this.editableDisk;
  }

  getServerBackupCost = task(async () => {
    let data = {
      serverid: this.server.id,
      enabled: true,
    };

    let result = await this.ajax.post(`${ENV.apiBaseUri}/server/estimatedbackupcost`, { data });

    return result.billing.estimated.total;
  });

  removeIpTask = task(async (ip, options) => {
    if (options["keep-ip"]) {
      await this.store.adapterFor("reserved-ip").unassignFromServer(ip.address);
    } else {
      await destroyRecord(ip);
    }
  });

  assignIpsTask = task(async (ips) => {
    let store = this.store;
    let project = this.project;
    let server = this.server;

    await RSVP.all(
      ips.map(async (ip) => {
        if (ip.get("isReserved")) {
          return store.adapterFor("reserved-ip").assignToServer(ip.address, server.id);
        }

        let reservedIp = store.createRecord("reserved-ip", {
          project,
          address: ip.get("address"),
        });

        try {
          await store.request(saveRecord(reservedIp));
          return await store.adapterFor("reserved-ip").assignToServer(reservedIp.address, server.id);
        } catch (error) {
          reservedIp.rollbackAttributes();
          throw error;
        }
      }),
    );
  });

  saveServerConfigurationTask = task(async () => {
    let server = this.server;
    let config = {
      disksize: this.editableDisk,
      memorysize: this.editableMemory,
      cpucores: this.editableCpu,
    };
    server.setProperties(config);
    server.lock("updating");
    await this.store.request(saveRecord(server));
  });

  updateBackupConfigurationTask = task(async () => {
    let backupEnabled = !this.server.backup.enabled;

    let response = await this.http.request({
      url: `${ENV.apiBaseUri}/server/backup`,
      method: "POST",
      body: {
        serverid: this.server.id,
        enabled: backupEnabled ? "yes" : "no",
      },
    });

    this.store.pushPayload("server", { server: response.content.server });
  });

  @action
  openServerAddIpsModal(version) {
    let project = this.project;
    let server = this.server;
    let task = this.assignIpsTask;
    this.modal.open("modals/server-add-ips", {
      project,
      server,
      task,
      version,
    });
  }

  setEditableCpu = (value) => {
    this.editableCpu = value;
  };

  setEditableMemory = (value) => {
    this.editableMemory = value;
  };

  setEditableDisk = (value) => {
    this.editableDisk = value;
  };
}
